import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { CircularProgress, colorSystem, Flexbox, TertiaryIconButton, Text } from '@luminovo/design-system';
import { Box, LinearProgress, Slide } from '@mui/material';
import * as Dialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { ChevronRightIcon, InboxIcon, XIcon } from 'lucide-react';
import React, { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    formatAccessoryCount,
    HintButton,
    injectCmdkStyles,
    KeyboardBadge,
    matchShortcut,
    useIsMac,
    useKeyboardNavigation,
    usePaletteAnalytics,
    usePaletteOpenEffect,
    VisuallyHidden,
} from '../../CommandPalette/shared';
import { CommandContext, CommandDefinition, CommandPaletteProps, isAccessoryCount, isIconConfig } from './types';

injectCmdkStyles();

/** Runs bulk actions on the selected items. */
export function CommandPalette<TItem = unknown, TSharedContext = unknown>({
    open,
    onOpenChange,
    commands,
    context,
    onClearSelection,
    placeholder,
    renderSelectionHeader,
    telemetry,
}: CommandPaletteProps<TItem, TSharedContext>): JSX.Element | null {
    const [pages, setPages] = useState<string[]>([]);
    // The command's ID, so only its item shows a spinner.
    const [loading, setLoading] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [quickLookCommandId, setQuickLookCommandId] = useState<string | null>(null);
    const [selectedCommandId, setSelectedCommandId] = useState<string | null>(null);
    const [confirmingCommand, setConfirmingCommand] = useState<CommandDefinition<TItem, TSharedContext> | null>(null);
    // Controlled, so Tab can move the selection.
    const [selectedValue, setSelectedValue] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);
    const listContainerRef = useRef<HTMLDivElement>(null);

    const searchTrackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const page = pages[pages.length - 1];

    const isMac = useIsMac();
    const { isKeyboardNavigating, setIsKeyboardNavigating, handleMouseMove } = useKeyboardNavigation();
    const { trackEvent } = usePaletteAnalytics({
        telemetry,
        paletteType: 'table',
    });

    useEffect(() => {
        return () => {
            if (searchTrackTimeoutRef.current) {
                clearTimeout(searchTrackTimeoutRef.current);
            }
        };
    }, []);

    usePaletteOpenEffect(
        open,
        () => trackEvent('opened', { trigger: 'button' }),
        useCallback(() => {
            setSearch('');
            setPages([]);
            setLoading(null);
            setQuickLookCommandId(null);
            setSelectedCommandId(null);
            setConfirmingCommand(null);
            setSelectedValue('');
            setIsKeyboardNavigating(false);
            if (searchTrackTimeoutRef.current) {
                clearTimeout(searchTrackTimeoutRef.current);
            }
        }, [setIsKeyboardNavigating]),
    );

    const fullContext: CommandContext<TItem, TSharedContext> = useMemo(
        () => ({
            ...context,
            close: () => {
                trackEvent('closed', { method: 'programmatic' });
                onOpenChange(false);
            },
            clearSelection: () => {
                trackEvent('selection_cleared', { source: 'command_palette' });
                onClearSelection?.();
            },
            goBack: () => {
                if (pages.length > 0) {
                    trackEvent('navigated_back', { method: 'programmatic' });
                    setPages((p) => p.slice(0, -1));
                }
            },
        }),
        [context, onOpenChange, onClearSelection, pages.length, trackEvent],
    );

    const resolveChildren = useCallback(
        (cmd: CommandDefinition<TItem, TSharedContext>): CommandDefinition<TItem, TSharedContext>[] | null => {
            if (!cmd.children) return null;
            if (typeof cmd.children === 'function') {
                return cmd.children(fullContext);
            }
            return cmd.children;
        },
        [fullContext],
    );

    const hasChildren = useCallback((cmd: CommandDefinition<TItem, TSharedContext>): boolean => {
        if (!cmd.children) return false;
        if (typeof cmd.children === 'function') {
            // Can't know without calling it.
            return true;
        }
        return cmd.children.length > 0;
    }, []);

    const { currentCommands, parentCommand } = useMemo(() => {
        if (!page) return { currentCommands: commands, parentCommand: null };

        const findChildrenAndParent = (
            cmds: CommandDefinition<TItem, TSharedContext>[],
        ): {
            children: CommandDefinition<TItem, TSharedContext>[];
            parent: CommandDefinition<TItem, TSharedContext>;
        } | null => {
            for (const cmd of cmds) {
                if (cmd.id === page) {
                    return { children: resolveChildren(cmd) ?? [], parent: cmd };
                }
                const resolvedChildren = resolveChildren(cmd);
                if (resolvedChildren) {
                    const found = findChildrenAndParent(resolvedChildren);
                    if (found) return found;
                }
            }
            return null;
        };

        const result = findChildrenAndParent(commands);
        return {
            currentCommands: result?.children ?? [],
            parentCommand: result?.parent ?? null,
        };
    }, [commands, page, resolveChildren]);

    const emptyPlaceholder = useMemo(() => {
        if (!parentCommand?.emptyPlaceholder || currentCommands.length > 0) return null;
        return parentCommand.emptyPlaceholder;
    }, [parentCommand, currentCommands.length]);

    const groupedCommands = useMemo(() => {
        const groups = new Map<string | null, CommandDefinition<TItem, TSharedContext>[]>();

        for (const cmd of currentCommands) {
            const isVisible = typeof cmd.visible === 'function' ? cmd.visible(fullContext) : (cmd.visible ?? true);

            if (!isVisible) continue;

            const groupName = cmd.group?.() ?? null;
            if (!groups.has(groupName)) {
                groups.set(groupName, []);
            }
            groups.get(groupName)!.push(cmd);
        }

        return Array.from(groups.entries());
    }, [currentCommands, fullContext]);

    const visibleCommands = useMemo(() => {
        return groupedCommands.flatMap(([, cmds]) => cmds);
    }, [groupedCommands]);

    const findCommandById = useCallback(
        (id: string): CommandDefinition<TItem, TSharedContext> | null => {
            return visibleCommands.find((cmd) => cmd.id === id) ?? null;
        },
        [visibleCommands],
    );

    const selectedCommand = useMemo(() => {
        if (!selectedCommandId) return null;
        return findCommandById(selectedCommandId);
    }, [selectedCommandId, findCommandById]);

    const quickLookCommand = useMemo(() => {
        if (!quickLookCommandId) return null;
        return findCommandById(quickLookCommandId);
    }, [quickLookCommandId, findCommandById]);

    const selectedCommandHasQuickLook = useMemo(() => {
        return selectedCommand?.quickLook !== undefined;
    }, [selectedCommand]);

    // Quick Look follows the selection.
    useEffect(() => {
        if (!quickLookCommandId || !selectedCommandId) return;
        if (quickLookCommandId === selectedCommandId) return;

        const newSelectedCommand = findCommandById(selectedCommandId);
        if (newSelectedCommand?.quickLook) {
            // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
            setQuickLookCommandId(selectedCommandId);
        } else {
            setQuickLookCommandId(null);
        }
    }, [selectedCommandId, quickLookCommandId, findCommandById]);

    const openQuickLook = useCallback(
        (commandId: string) => {
            const cmd = findCommandById(commandId);
            if (!cmd?.quickLook) return;
            trackEvent('quick_look_opened', { commandId });
            setQuickLookCommandId(commandId);
        },
        [findCommandById, trackEvent],
    );

    const closeQuickLook = useCallback(
        (method: 'arrow_left' | 'escape' | 'command_change' | 'programmatic') => {
            if (!quickLookCommandId) return;
            trackEvent('quick_look_closed', { commandId: quickLookCommandId, method });
            setQuickLookCommandId(null);
            setTimeout(() => inputRef.current?.focus(), 0);
        },
        [quickLookCommandId, trackEvent],
    );

    const handleSelect = useCallback(
        async (cmd: CommandDefinition<TItem, TSharedContext>) => {
            const isDisabled = typeof cmd.disabled === 'function' ? cmd.disabled(fullContext) : (cmd.disabled ?? false);
            const commandGroup = cmd.group?.() ?? null;

            if (isDisabled || loading) return;

            if (hasChildren(cmd)) {
                resolveChildren(cmd);
                trackEvent('navigated_to_subcommands', { commandId: cmd.id });
                setPages((p) => [...p, cmd.id]);
                setSearch('');
                setQuickLookCommandId(null);
                return;
            }

            if (cmd.confirmation) {
                setConfirmingCommand(cmd);
                setSearch('');
                setQuickLookCommandId(null);
                return;
            }

            if (cmd.onSelect) {
                trackEvent('command_executed', { commandId: cmd.id, commandGroup });
                setLoading(cmd.id);

                try {
                    await cmd.onSelect(fullContext);
                    trackEvent('command_succeeded', { commandId: cmd.id, commandGroup });
                    if (cmd.closeOnComplete === true) {
                        onOpenChange(false);
                    }
                } catch (error) {
                    trackEvent('command_failed', { commandId: cmd.id, commandGroup });
                    console.error('Command execution failed:', error);
                } finally {
                    setLoading(null);
                }
            }
        },
        [fullContext, loading, onOpenChange, trackEvent, hasChildren, resolveChildren],
    );

    const handleConfirm = useCallback(async () => {
        if (!confirmingCommand) return;
        const onSelect = confirmingCommand.onSelect;
        if (!onSelect) return;

        const cmd = confirmingCommand;
        const commandGroup = cmd.group?.() ?? null;

        trackEvent('command_executed', { commandId: cmd.id, commandGroup });
        setLoading(cmd.id);

        try {
            await onSelect(fullContext);
            trackEvent('command_succeeded', { commandId: cmd.id, commandGroup });
            if (cmd.closeOnComplete === true) {
                onOpenChange(false);
            } else {
                setConfirmingCommand(null);
            }
        } catch (error) {
            trackEvent('command_failed', { commandId: cmd.id, commandGroup });
            console.error('Command execution failed:', error);
        } finally {
            setLoading(null);
        }
    }, [confirmingCommand, fullContext, onOpenChange, trackEvent]);

    const handleCancelConfirmation = useCallback(() => {
        setConfirmingCommand(null);
    }, []);

    const findCommandByShortcut = useCallback(
        (e: React.KeyboardEvent): CommandDefinition<TItem, TSharedContext> | null => {
            for (const cmd of currentCommands) {
                if (!cmd.shortcut || cmd.shortcut.length === 0) continue;

                const isVisible = typeof cmd.visible === 'function' ? cmd.visible(fullContext) : (cmd.visible ?? true);
                if (!isVisible) continue;

                const isDisabled =
                    typeof cmd.disabled === 'function' ? cmd.disabled(fullContext) : (cmd.disabled ?? false);
                if (isDisabled) continue;

                if (matchShortcut(cmd.shortcut, e, isMac)) return cmd;
            }
            return null;
        },
        [currentCommands, fullContext, isMac],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (['ArrowDown', 'ArrowUp', 'Tab'].includes(e.key)) {
                setIsKeyboardNavigating(true);
            }

            // Tab and Shift+Tab move between items, wrapping around, as in Linear.
            if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();

                const items = listContainerRef.current?.querySelectorAll('[cmdk-item]:not([data-disabled="true"])');
                if (!items || items.length === 0) return;

                const currentIndex = Array.from(items).findIndex(
                    (item) => item.getAttribute('data-selected') === 'true',
                );

                let nextIndex: number;
                if (e.shiftKey) {
                    nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
                } else {
                    nextIndex = currentIndex >= items.length - 1 ? 0 : currentIndex + 1;
                }

                const nextItem = items[nextIndex];
                const nextValue = nextItem?.getAttribute('data-value');
                if (nextValue) {
                    setSelectedValue(nextValue);
                }
                return;
            }

            if (e.key === 'ArrowRight' && selectedCommandId && selectedCommandHasQuickLook && !quickLookCommandId) {
                e.preventDefault();
                e.stopPropagation();
                openQuickLook(selectedCommandId);
                return;
            }

            if (e.key === 'ArrowLeft' && quickLookCommandId) {
                e.preventDefault();
                e.stopPropagation();
                closeQuickLook('arrow_left');
                return;
            }

            if (e.key === 'Backspace' && !search && pages.length > 0) {
                e.preventDefault();
                trackEvent('navigated_back', { method: 'backspace' });
                setPages((p) => p.slice(0, -1));
                return;
            }

            const matchedCommand = findCommandByShortcut(e);
            if (matchedCommand) {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(matchedCommand);
            }
        },
        [
            search,
            pages.length,
            findCommandByShortcut,
            handleSelect,
            trackEvent,
            selectedCommandId,
            selectedCommandHasQuickLook,
            quickLookCommandId,
            openQuickLook,
            closeQuickLook,
        ],
    );

    const handleClose = useCallback(() => {
        trackEvent('closed', { method: 'button' });
        onOpenChange(false);
    }, [onOpenChange, trackEvent]);

    const handleGoBack = useCallback(() => {
        if (pages.length > 0) {
            trackEvent('navigated_back', { method: 'button' });
            setPages((p) => p.slice(0, -1));
        }
    }, [pages.length, trackEvent]);

    // Scores only 1 or 0, so cmdk keeps the commands' order.
    const customFilter = useCallback((value: string, search: string) => {
        if (!search) return 1;
        return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
    }, []);

    // Refs, so handleOpenChange reads the current state synchronously.
    const pagesRef = useRef(pages);
    pagesRef.current = pages;

    const quickLookCommandIdRef = useRef(quickLookCommandId);
    quickLookCommandIdRef.current = quickLookCommandId;

    const confirmingCommandRef = useRef(confirmingCommand);
    confirmingCommandRef.current = confirmingCommand;

    // Escape first closes Quick Look, the confirmation or a subpage, and only then the palette.
    const handleOpenChange = useCallback(
        (newOpen: boolean) => {
            if (!newOpen && quickLookCommandIdRef.current) {
                trackEvent('quick_look_closed', { commandId: quickLookCommandIdRef.current, method: 'escape' });
                setQuickLookCommandId(null);
                setTimeout(() => inputRef.current?.focus(), 0);
                return;
            }
            if (!newOpen && confirmingCommandRef.current) {
                setConfirmingCommand(null);
                return;
            }
            if (!newOpen && pagesRef.current.length > 0) {
                trackEvent('navigated_back', { method: 'escape' });
                setPages((p) => p.slice(0, -1));
                setTimeout(() => inputRef.current?.focus(), 0);
                return;
            }
            if (!newOpen) {
                trackEvent('closed', { method: 'escape' });
            }
            onOpenChange(newOpen);
        },
        [onOpenChange, trackEvent],
    );

    if (!open) return null;

    return (
        <Command.Dialog
            open={open}
            onOpenChange={handleOpenChange}
            label={t`Command palette`}
            onKeyDown={handleKeyDown}
            onMouseMove={handleMouseMove}
            filter={customFilter}
            // The confirmation view's Confirm and Cancel values are internal ids, which search text would filter out.
            shouldFilter={!confirmingCommand}
            loop
            value={selectedValue}
            onValueChange={setSelectedValue}
            className={isKeyboardNavigating ? 'keyboard-navigating' : undefined}
        >
            <VisuallyHidden>
                <Dialog.Title>{t`Command palette`}</Dialog.Title>
                <Dialog.Description>{t`Select an action to apply to the selected items`}</Dialog.Description>
            </VisuallyHidden>
            <TertiaryIconButton
                onClick={handleClose}
                size="small"
                style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                }}
                aria-label={t`Close`}
            >
                <XIcon />
            </TertiaryIconButton>
            <Box
                sx={{
                    padding: '10px 20px 0 20px',
                }}
            >
                {renderSelectionHeader ? (
                    renderSelectionHeader({
                        selectedItems: context.selectedItems,
                        isSingleSelection: context.selectedItems.length === 1,
                    })
                ) : (
                    <Text variant="body-small" color={colorSystem.neutral[6]}>
                        {context.selectedItems.length === 1 ? (
                            <Trans>1 item selected</Trans>
                        ) : (
                            <Trans>{context.selectedItems.length} items selected</Trans>
                        )}
                    </Text>
                )}
            </Box>
            <Box sx={{ position: 'relative' }}>
                <Command.Input
                    ref={inputRef}
                    value={search}
                    onValueChange={(value) => {
                        setSearch(value);
                        if (value) {
                            if (searchTrackTimeoutRef.current) {
                                clearTimeout(searchTrackTimeoutRef.current);
                            }
                            searchTrackTimeoutRef.current = setTimeout(() => {
                                trackEvent('searched', { query: value });
                            }, 300);
                        }
                    }}
                    placeholder={placeholder ?? t`Search actions...`}
                    autoFocus
                />
                {loading && (
                    <LinearProgress
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                        }}
                    />
                )}
            </Box>
            <Box className="cmdk-quick-look-container">
                {confirmingCommand && (
                    <Box className="cmdk-quick-look-commands" ref={listContainerRef}>
                        <Box sx={{ padding: '16px 20px 8px 20px' }}>
                            <Text variant="body-semibold" color={colorSystem.neutral[9]}>
                                {confirmingCommand.confirmation?.title()}
                            </Text>
                            {confirmingCommand.confirmation?.description && (
                                <Text
                                    variant="body-small"
                                    color={colorSystem.neutral[6]}
                                    style={{ marginTop: 4, display: 'block' }}
                                >
                                    {confirmingCommand.confirmation.description()}
                                </Text>
                            )}
                        </Box>

                        <Command.List>
                            <Command.Empty>
                                <Trans>No actions found</Trans>
                            </Command.Empty>

                            <Command.Item
                                value="__confirmation_confirm__"
                                onSelect={handleConfirm}
                                disabled={loading !== null}
                            >
                                {loading === confirmingCommand.id && (
                                    <CircularProgress size="small" sx={{ flexShrink: 0 }} />
                                )}
                                <Box sx={{ flex: 1 }}>
                                    <Text
                                        variant="body"
                                        color={
                                            confirmingCommand.confirmation?.variant === 'destructive'
                                                ? colorSystem.red[7]
                                                : colorSystem.primary[7]
                                        }
                                    >
                                        {confirmingCommand.confirmation?.confirmLabel?.() ?? t`Confirm`}
                                    </Text>
                                </Box>
                                {confirmingCommand.accessory && (
                                    <Text variant="body-small" color={colorSystem.neutral[6]}>
                                        {(() => {
                                            const raw =
                                                typeof confirmingCommand.accessory === 'function'
                                                    ? confirmingCommand.accessory(fullContext)
                                                    : confirmingCommand.accessory;
                                            return isAccessoryCount(raw) ? formatAccessoryCount(raw) : raw;
                                        })()}
                                    </Text>
                                )}
                            </Command.Item>

                            <Command.Item value="__confirmation_cancel__" onSelect={handleCancelConfirmation}>
                                <Text variant="body" color={colorSystem.neutral[6]}>
                                    ← {confirmingCommand.confirmation?.cancelLabel?.() ?? t`Cancel`}
                                </Text>
                            </Command.Item>
                        </Command.List>
                    </Box>
                )}

                {!confirmingCommand && (
                    <Box className="cmdk-quick-look-commands" ref={listContainerRef}>
                        {parentCommand &&
                            (() => {
                                const headerTitle = parentCommand.subPageHeader?.title() ?? parentCommand.label();
                                const headerDescription =
                                    parentCommand.subPageHeader?.description?.() ?? parentCommand.description?.();
                                return (
                                    <Box sx={{ padding: '12px 20px 4px 20px' }}>
                                        <Text variant="body-semibold" color={colorSystem.neutral[9]}>
                                            {headerTitle}
                                        </Text>
                                        {headerDescription && (
                                            <Text
                                                variant="body-small"
                                                color={colorSystem.neutral[6]}
                                                style={{ marginTop: 2, display: 'block' }}
                                            >
                                                {headerDescription}
                                            </Text>
                                        )}
                                    </Box>
                                );
                            })()}

                        <Command.List>
                            <Command.Empty>
                                <Trans>No actions found</Trans>
                            </Command.Empty>

                            {emptyPlaceholder && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '32px 16px',
                                        minHeight: '200px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <InboxIcon size={48} color={colorSystem.neutral[4]} />
                                    <Flexbox
                                        gap="4px"
                                        sx={{
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Text variant="body-semibold" color={colorSystem.neutral[7]}>
                                            {emptyPlaceholder.title()}
                                        </Text>
                                        {emptyPlaceholder.description && (
                                            <Text variant="body-small" color={colorSystem.neutral[6]}>
                                                {emptyPlaceholder.description()}
                                            </Text>
                                        )}
                                    </Flexbox>
                                </Box>
                            )}

                            {groupedCommands.map(([groupName, cmds]) => {
                                const items = cmds.map((cmd) => (
                                    <CommandItem
                                        key={cmd.id}
                                        command={cmd}
                                        context={fullContext}
                                        loading={loading === cmd.id}
                                        isMac={isMac}
                                        hasQuickLookOpen={quickLookCommandId === cmd.id}
                                        hasChildren={hasChildren(cmd)}
                                        onSelect={() => handleSelect(cmd)}
                                        onSelectedChange={(selected) => {
                                            if (selected) {
                                                setSelectedCommandId(cmd.id);
                                            }
                                        }}
                                        onOpenQuickLook={() => {
                                            if (quickLookCommandId === cmd.id) {
                                                closeQuickLook('programmatic');
                                            } else {
                                                openQuickLook(cmd.id);
                                            }
                                        }}
                                    />
                                ));

                                if (groupName) {
                                    return (
                                        <Command.Group key={groupName} heading={groupName}>
                                            {items}
                                        </Command.Group>
                                    );
                                }

                                return <React.Fragment key="ungrouped">{items}</React.Fragment>;
                            })}

                            {pages.length > 0 && (
                                <Box
                                    sx={{
                                        marginTop: '8px',
                                        paddingTop: '8px',
                                        borderTop: `1px solid ${colorSystem.neutral[2]}`,
                                    }}
                                >
                                    <Command.Item value="__back__" onSelect={handleGoBack}>
                                        <Text variant="body" color={colorSystem.neutral[6]}>
                                            ← <Trans>Back</Trans>
                                        </Text>
                                    </Command.Item>
                                </Box>
                            )}
                        </Command.List>
                    </Box>
                )}

                <Slide direction="left" in={!!quickLookCommand} mountOnEnter unmountOnExit>
                    <Box className="cmdk-quick-look-panel" sx={{ width: quickLookCommand?.quickLook?.width ?? 360 }}>
                        {quickLookCommand && (
                            <>
                                <Box className="cmdk-quick-look-panel-header">
                                    <Flexbox
                                        gap="2px"
                                        style={{ flex: 1, minWidth: 0 }}
                                        sx={{
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Text variant="body-semibold" color={colorSystem.neutral[8]}>
                                            {quickLookCommand.label()}
                                        </Text>
                                        {quickLookCommand.description && (
                                            <Text variant="body-small" color={colorSystem.neutral[6]}>
                                                {quickLookCommand.description()}
                                            </Text>
                                        )}
                                    </Flexbox>
                                    <TertiaryIconButton
                                        onClick={() => closeQuickLook('programmatic')}
                                        size="small"
                                        aria-label={t`Close quick look`}
                                    >
                                        <XIcon />
                                    </TertiaryIconButton>
                                </Box>
                                <Box className="cmdk-quick-look-panel-content">
                                    {quickLookCommand.quickLook?.render(fullContext)}
                                </Box>
                            </>
                        )}
                    </Box>
                </Slide>
            </Box>
            <Box
                sx={{
                    padding: '12px 20px',
                    borderTop: `1px solid ${colorSystem.neutral[2]}`,
                }}
            >
                <Flexbox
                    gap="8px"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Flexbox
                        gap="12px"
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <HintButton badge="↑↓" label={<Trans>navigate</Trans>} />
                        <HintButton badge="↵" label={<Trans>select</Trans>} />
                        {pages.length > 0 ? (
                            <HintButton badge="esc" label={<Trans>back</Trans>} onClick={handleGoBack} />
                        ) : (
                            <HintButton badge="esc" label={<Trans>close</Trans>} onClick={handleClose} />
                        )}
                    </Flexbox>
                    <Flexbox
                        gap="12px"
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {quickLookCommandId ? (
                            <HintButton
                                badge="←"
                                label={<Trans>close preview</Trans>}
                                onClick={() => closeQuickLook('programmatic')}
                            />
                        ) : selectedCommandHasQuickLook ? (
                            <HintButton
                                badge="→"
                                label={<Trans>preview</Trans>}
                                onClick={() => selectedCommandId && openQuickLook(selectedCommandId)}
                            />
                        ) : null}
                        <Flexbox
                            gap="2px"
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <KeyboardBadge>{isMac ? '⌘' : 'Ctrl'}</KeyboardBadge>
                            <KeyboardBadge>K</KeyboardBadge>
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            </Box>
        </Command.Dialog>
    );
}

type CommandItemProps<TItem, TSharedContext> = {
    command: CommandDefinition<TItem, TSharedContext>;
    context: CommandContext<TItem, TSharedContext>;
    loading: boolean;
    isMac: boolean;
    hasQuickLookOpen: boolean;
    hasChildren: boolean;
    onSelect: () => void;
    onSelectedChange: (selected: boolean) => void;
    onOpenQuickLook: () => void;
};

function CommandItem<TItem, TSharedContext>({
    command,
    context,
    loading,
    isMac,
    hasQuickLookOpen,
    hasChildren,
    onSelect,
    onSelectedChange,
    onOpenQuickLook,
}: CommandItemProps<TItem, TSharedContext>): JSX.Element {
    const isDisabled = typeof command.disabled === 'function' ? command.disabled(context) : (command.disabled ?? false);
    const itemRef = useRef<HTMLDivElement>(null);
    const lastSelectedRef = useRef<boolean | null>(null);
    const [isHoveringPreview, setIsHoveringPreview] = useState(false);
    // In a ref, so the effect below runs once.
    const onSelectedChangeRef = useRef(onSelectedChange);
    onSelectedChangeRef.current = onSelectedChange;

    const hasQuickLook = !!command.quickLook;
    const label = command.label();
    const description = command.description?.();

    const quickLookPreview = hasQuickLook
        ? typeof command.quickLook?.preview === 'function'
            ? command.quickLook.preview(context)
            : command.quickLook?.preview
        : undefined;

    // Selection lives in cmdk's `data-selected` attribute, so an observer watches it.
    useEffect(() => {
        const element = itemRef.current;
        if (!element) return;

        const handleSelectionChange = (selected: boolean) => {
            if (lastSelectedRef.current !== selected) {
                lastSelectedRef.current = selected;
                onSelectedChangeRef.current(selected);
            }
        };

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-selected') {
                    const selected = element.getAttribute('data-selected') === 'true';
                    handleSelectionChange(selected);
                }
            }
        });

        observer.observe(element, { attributes: true, attributeFilter: ['data-selected'] });

        const initialSelected = element.getAttribute('data-selected') === 'true';
        handleSelectionChange(initialSelected);

        return () => observer.disconnect();
    }, []); // Empty deps - we use refs for everything

    const rawAccessory = typeof command.accessory === 'function' ? command.accessory(context) : command.accessory;

    const accessoryContent = isAccessoryCount(rawAccessory) ? formatAccessoryCount(rawAccessory) : rawAccessory;

    // cmdk filters on `value`, so it includes the keywords.
    const value = [label, description, ...(command.keywords ?? [])].filter(Boolean).join(' ');

    let defaultIcon: React.ReactNode = null;
    let hoverIcon: React.ReactNode = null;
    if (command.icon) {
        if (isIconConfig(command.icon)) {
            defaultIcon = command.icon.default;
            hoverIcon = command.icon.hover ?? command.icon.default;
        } else {
            defaultIcon = command.icon;
            hoverIcon = command.icon;
        }
    }

    return (
        <Command.Item
            ref={itemRef}
            value={value}
            keywords={command.keywords}
            onSelect={onSelect}
            disabled={isDisabled || loading}
        >
            {command.icon && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        marginTop: '2px',
                        color: colorSystem.neutral[6],
                    }}
                >
                    <Box className="cmdk-icon-default">{defaultIcon}</Box>
                    <Box className="cmdk-icon-hover">{hoverIcon}</Box>
                </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Text variant="body" color={colorSystem.neutral[9]}>
                    {label}
                </Text>
                {description && (
                    <Text variant="body-small" color={colorSystem.neutral[7]} style={{ lineHeight: 1.3 }}>
                        {description}
                    </Text>
                )}
            </Box>
            {loading && <CircularProgress size="small" sx={{ marginTop: '2px' }} />}
            {accessoryContent && !loading && !(hasQuickLook && !hasChildren) && (
                <Text variant="body-small" color={colorSystem.neutral[7]} style={{ marginTop: '2px', flexShrink: 0 }}>
                    {accessoryContent}
                </Text>
            )}
            {hasQuickLook && !loading && !hasChildren && (
                <Box
                    className="cmdk-quick-look-preview"
                    onMouseEnter={() => setIsHoveringPreview(true)}
                    onMouseLeave={() => setIsHoveringPreview(false)}
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenQuickLook();
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginTop: '2px',
                        padding: isHoveringPreview || hasQuickLookOpen ? '2px 8px' : '2px 4px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        backgroundColor: hasQuickLookOpen
                            ? colorSystem.primary[1]
                            : isHoveringPreview
                              ? colorSystem.neutral[2]
                              : 'transparent',
                        color: hasQuickLookOpen ? colorSystem.primary[7] : colorSystem.neutral[7],
                        '&:hover': {
                            backgroundColor: hasQuickLookOpen ? colorSystem.primary[2] : colorSystem.neutral[2],
                        },
                    }}
                >
                    {isHoveringPreview || hasQuickLookOpen ? (
                        <>
                            <Text
                                variant="body-small"
                                color={hasQuickLookOpen ? colorSystem.primary[7] : colorSystem.neutral[8]}
                                style={{ fontWeight: 500, whiteSpace: 'nowrap' }}
                            >
                                {hasQuickLookOpen ? t`Close` : t`Preview`}
                            </Text>
                            <ChevronRightIcon
                                size={16}
                                color={hasQuickLookOpen ? colorSystem.primary[6] : colorSystem.neutral[6]}
                                style={{
                                    transform: hasQuickLookOpen ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.15s ease',
                                }}
                            />
                        </>
                    ) : (
                        <Text variant="body-small" color={colorSystem.neutral[6]} style={{ whiteSpace: 'nowrap' }}>
                            {quickLookPreview ?? accessoryContent ?? t`Preview`}
                        </Text>
                    )}
                </Box>
            )}
            {hasChildren && !loading && (
                <ChevronRightIcon size={18} color={colorSystem.neutral[6]} style={{ marginTop: '2px' }} />
            )}
            {command.shortcut && !loading && !hasChildren && !hasQuickLook && (
                <Flexbox
                    gap="2px"
                    sx={{
                        alignItems: 'center',
                        marginTop: '2px',
                    }}
                >
                    {command.shortcut.map((key, index) => (
                        <KeyboardBadge key={index}>{key === '⌘' && !isMac ? 'Ctrl' : key}</KeyboardBadge>
                    ))}
                </Flexbox>
            )}
        </Command.Item>
    );
}
